import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { Clock, Check } from 'lucide-react';
import { TripInquiryModal } from './TripInquiryModal';
const categories = ['River Cruises', 'Europe', 'USA/Canada', 'Holiday Tours', 'Bucket List'];
const trips = {
  'River Cruises': [{
    title: 'Danube Christmas Markets',
    duration: '8 Days',
    image: 'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=800&auto=format&fit=crop',
    highlights: ['Vienna & Budapest', 'Christmas Markets', 'Luxury River Ship']
  }, {
    title: 'Rhine Valley Romance',
    duration: '10 Days',
    image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800&auto=format&fit=crop',
    highlights: ['Castles of the Rhine', 'Amsterdam Canals', 'Wine Tasting']
  }, {
    title: 'Blue Danube Discovery',
    duration: '11 Days',
    image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800&auto=format&fit=crop',
    highlights: ['Budapest to Prague', 'Abbey of Melk', 'Bratislava Tour']
  }],
  Europe: [{
    title: "Italy's Greatest Hits",
    duration: '12 Days',
    image: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&auto=format&fit=crop',
    highlights: ['Rome, Florence, Venice', 'Tuscany Wine Tasting', 'Colosseum Tour']
  }, {
    title: 'Ireland & Scotland',
    duration: '14 Days',
    image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=800&auto=format&fit=crop',
    highlights: ['Ring of Kerry', 'Edinburgh Castle', "Giant's Causeway"]
  }, {
    title: 'Alpine Swiss Villages',
    duration: '9 Days',
    image: 'https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&auto=format&fit=crop',
    highlights: ['Matterhorn View', 'Glacier Express', 'Lucerne Boat Ride']
  }],
  'USA/Canada': [{
    title: 'National Parks Explorer',
    duration: '10 Days',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&auto=format&fit=crop',
    highlights: ['Yellowstone', 'Grand Teton', 'Mt. Rushmore']
  }, {
    title: 'Canadian Rockies',
    duration: '9 Days',
    image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&auto=format&fit=crop',
    highlights: ['Banff & Jasper', 'Lake Louise', 'Icefields Parkway']
  }, {
    title: 'New England Fall Foliage',
    duration: '8 Days',
    image: 'https://images.unsplash.com/photo-1445346366695-5bf62de05412?w=800&auto=format&fit=crop',
    highlights: ['Vermont Maple Farm', 'Maine Coast', 'White Mountains']
  }],
  'Holiday Tours': [{
    title: 'Christmas in Bavaria',
    duration: '7 Days',
    image: 'https://images.unsplash.com/photo-1544986581-efac024faf62?w=800&auto=format&fit=crop',
    highlights: ['Munich Markets', 'Neuschwanstein', 'Salzburg Day Trip']
  }, {
    title: 'New Year in Vienna',
    duration: '6 Days',
    image: 'https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=800&auto=format&fit=crop',
    highlights: ['Imperial Ball', 'Strauss Concert', 'Schonbrunn Palace']
  }, {
    title: "Rose Parade New Year's",
    duration: '5 Days',
    image: 'https://images.unsplash.com/photo-1534430480872-3498386e7856?w=800&auto=format&fit=crop',
    highlights: ['Grandstand Seats', 'Float Viewing', 'Hollywood Tour']
  }],
  'Bucket List': [{
    title: 'Holy Land Pilgrimage',
    duration: '10 Days',
    image: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=800&auto=format&fit=crop',
    highlights: ['Jerusalem', 'Sea of Galilee', 'Bethlehem']
  }, {
    title: 'Machu Picchu & Galapagos',
    duration: '14 Days',
    image: 'https://images.unsplash.com/photo-1587595431973-160d0d94add1?w=800&auto=format&fit=crop',
    highlights: ['Sacred Valley', 'Inca Ruins', 'Wildlife Cruise']
  }, {
    title: 'South Africa Safari',
    duration: '12 Days',
    image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=800&auto=format&fit=crop',
    highlights: ['Kruger Park', 'Cape Town', 'Winelands']
  }]
};
export function FeaturedTrips() {
  const [activeTab, setActiveTab] = useState('Europe');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<{
    title: string;
    duration: string;
  } | null>(null);
  const handleRequestInfo = (trip: {
    title: string;
    duration: string;
  }) => {
    setSelectedTrip(trip);
    setIsModalOpen(true);
  };
  return <section id="featured-trips" className="py-20 bg-white" data-id="element-15">
      <div className="container mx-auto px-4" data-id="element-16">
        <div className="text-center mb-12" data-id="element-17">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary mb-4" data-id="element-18">
            Explore our group trips
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-19">
            Browse our most popular itineraries for 2026. Every trip includes
            our signature service and organizer support.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12" data-id="element-20">
          {categories.map(category => <button key={category} onClick={() => setActiveTab(category)} className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 relative ${activeTab === category ? 'text-white' : 'text-gray-600 hover:text-primary hover:bg-gray-100'}`} data-id="element-21">
              {activeTab === category && <motion.div layoutId="activeTab" className="absolute inset-0 bg-teal rounded-full" transition={{
            type: 'spring',
            bounce: 0.2,
            duration: 0.6
          }} data-id="element-22" />}
              <span className="relative z-10" data-id="element-23">{category}</span>
            </button>)}
        </div>

        {/* Trip Cards */}
        <div className="min-h-[400px]" data-id="element-24">
          <AnimatePresence mode="wait" data-id="element-25">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-26">
              {trips[activeTab as keyof typeof trips].map((trip, index) => <div key={index} className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col h-full" data-id="element-27">
                  <div className="relative h-56 overflow-hidden" data-id="element-28">
                    <img src={trip.image} alt={trip.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" data-id="element-29" />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-primary flex items-center shadow-sm" data-id="element-30">
                      <Clock size={14} className="mr-1" data-id="element-31" />
                      {trip.duration}
                    </div>
                  </div>

                  <div className="p-6 flex-1 flex flex-col" data-id="element-32">
                    <h3 className="text-xl font-bold text-primary mb-4 group-hover:text-teal transition-colors" data-id="element-33">
                      {trip.title}
                    </h3>

                    <ul className="space-y-2 mb-6 flex-1" data-id="element-34">
                      {trip.highlights.map((highlight, idx) => <li key={idx} className="flex items-start text-sm text-gray-600" data-id="element-35">
                          <Check size={16} className="text-teal mr-2 mt-0.5 flex-shrink-0" data-id="element-36" />
                          {highlight}
                        </li>)}
                    </ul>

                    <Button variant="outline" fullWidth className="mt-auto" onClick={() => handleRequestInfo({
                  title: trip.title,
                  duration: trip.duration
                })} data-id="element-37">
                      Request Info
                    </Button>
                  </div>
                </div>)}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-12 text-center flex flex-col sm:flex-row justify-center gap-4" data-id="element-38">
          <a href="#" className="text-primary font-medium hover:text-teal underline underline-offset-4" data-id="element-39">
            See More Trips
          </a>
          <span className="hidden sm:inline text-gray-300" data-id="element-40">|</span>
          <Button variant="ghost" size="sm" className="text-teal hover:bg-teal-50" data-id="element-41">
            View Online Brochure
          </Button>
        </div>
      </div>

      {/* Trip Inquiry Modal */}
      <TripInquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} tripTitle={selectedTrip?.title} tripDuration={selectedTrip?.duration} data-id="element-42" />
    </section>;
}